"use client";

import React, { useEffect, useRef, useState } from "react";

import styles from "./CasesDucks.module.scss";
import CasesStyles from "@/components/Cases/CasesGrid/CasesGrid.module.scss";

import gsap from "gsap";

import { Canvas, useThree } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { default as CommonDuck } from "@/components/common/3D/Duck";

export default function CasesDucks() {
  const canvasRef = useRef(null);

  return (
    <div ref={canvasRef} style={{ pointerEvents: "none" }}>
      <Canvas
        style={{
          position: "absolute",
          width: "100%",
          height: "100vh",
          top: 0,
          zIndex: 10,
          pointerEvents: "none",
        }}
        gl={{
          stencil: false,
          autoClear: true,
          alpha: true,
          antialias: true,
          powerPreference: "high-performance",
        }}
        camera={{
          position: [0, 0, 15],
          fov: 50,
        }}
        eventSource={null}
        className={styles.duckCanvas}
      >
        <Float floatIntensity={0.5}>
          <Duck triggerRef={canvasRef}></Duck>
        </Float>
      </Canvas>
    </div>
  );
}

const Duck = ({ triggerRef }) => {
  const { viewport } = useThree();

  const duckRef = useRef(null);

  const [scale, setScale] = useState(0);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [rotationY, setRotationY] = useState(0);

  const modelData = useRef({
    rotationX: 0,
    rotationY: 0,
    scale: 0,
    activeBounds: false,
  });

  const [gotResponsive, setGotResponsive] = useState(false);

  const getResponsive = () => {
    const breakpoints = [
      {
        breakpoint: "screen and (min-width: 1000px)",
        scale: viewport.width / 2.8,
        x: viewport.width / 3.25,
        y: -viewport.height / 4.75,
        rotationY: -Math.PI * 0.3,
      },
      {
        breakpoint: "screen and (min-width: 1200px)",
        scale: viewport.width / 3.3,
        x: viewport.width / 3.5,
        y: -viewport.height / 4.5,
        rotationY: -Math.PI * 0.3,
      },
      {
        breakpoint: "screen and (min-width: 1600px)",
        scale: viewport.width / 3,
        x: viewport.width / 4,
        y: -viewport.height / 4.5,
        rotationY: -Math.PI * 0.3,
      },
    ];

    let result;

    breakpoints.forEach((breakpoint) => {
      if (window.matchMedia(breakpoint.breakpoint).matches) {
        result = breakpoint;
      }
    });

    setScale(result.scale);
    setPositionX(result.x);
    setPositionY(result.y);
    setRotationY(result.rotationY);
    modelData.current.rotationY = result.rotationY;
    modelData.current.scale = result.scale;
    windowWidth.current = window.innerWidth;

    if (!gotResponsive) {
      setGotResponsive(true);
    }
  };

  const windowWidth = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia("screen and (min-width: 1000px)").matches) {
        getResponsive();
      }
      windowWidth.current = window.innerWidth;
    };

    getResponsive();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (
      duckRef.current !== null &&
      gotResponsive &&
      windowWidth.current >= 1000
    ) {
      const mm = gsap.matchMedia();

      const endTrigger = document.querySelector(`.${CasesStyles.wrapper}`);

      gsap.set(triggerRef.current, { zIndex: 10 });

      const pinTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: triggerRef.current,
          start: "top top",
          endTrigger: endTrigger,
          end: "bottom+=48 bottom",
          pin: true,
          pinSpacing: false,
          scrub: true,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
        },
      });

      const modelTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: endTrigger,
          start: "top bottom",
          end: "top center",
          scrub: true,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
        },
      });

      mm.add("(min-width: 1000px) and (max-width: 1199px)", () => {
        modelTimeline
          .to(duckRef.current.position, {
            x: 0.65,
            y: -viewport.height / 2.25,
            ease: "sine.out",
          })
          .to(
            duckRef.current.rotation,
            {
              y: Math.PI * -0.05,
              x: -0.2,
              ease: "sine.out",
              onStart: () => {
                modelData.current.activeBounds = true;
              },
              onReverseComplete: () => {
                modelData.current.activeBounds = false;
              },
              onUpdate: () => {
                modelData.current.rotationY = duckRef.current.rotation.y;
              },
            },
            "<"
          )
          .to(
            duckRef.current.scale,
            {
              x: scale / 1.5,
              y: scale / 1.5,
              z: scale / 1.5,
              ease: "sine.out",
              onUpdate: () => {
                modelData.current.scale = duckRef.current.scale.z;
              },
            },
            "<"
          );
      });

      mm.add("(min-width: 1200px) and (max-width: 1599px)", () => {
        modelTimeline
          .to(duckRef.current.position, {
            x: 1.5,
            y: -viewport.height / 2.25,
            ease: "sine.out",
          })
          .to(
            duckRef.current.rotation,
            {
              y: Math.PI * -0.05,
              x: -0.2,

              ease: "sine.out",
              onStart: () => {
                modelData.current.activeBounds = true;
              },
              onReverseComplete: () => {
                modelData.current.activeBounds = false;
              },
              onUpdate: () => {
                modelData.current.rotationY = duckRef.current.rotation.y;
              },
            },
            "<"
          )
          .to(
            duckRef.current.scale,
            {
              x: scale / 1.4,
              y: scale / 1.4,
              z: scale / 1.4,
              ease: "sine.out",
              onUpdate: () => {
                modelData.current.scale = duckRef.current.scale.z;
              },
            },
            "<"
          );
      });

      mm.add("(min-width: 1600px)", () => {
        modelTimeline
          .to(duckRef.current.position, {
            x: 1.3,
            y: -viewport.height / 2.25,
            ease: "sine.out",
          })
          .to(
            duckRef.current.rotation,
            {
              y: Math.PI * -0.05,
              x: -0.2,
              ease: "sine.out",
              onStart: () => {
                modelData.current.activeBounds = true;
              },
              onReverseComplete: () => {
                modelData.current.activeBounds = false;
              },
              onUpdate: () => {
                modelData.current.rotationY = duckRef.current.rotation.y;
              },
            },
            "<"
          )
          .to(
            duckRef.current.scale,
            {
              x: scale / 1.65,
              y: scale / 1.65,
              z: scale / 1.65,
              ease: "sine.out",
              onUpdate: () => {
                modelData.current.scale = duckRef.current.scale.z;
              },
            },
            "<"
          );
      });

      return () => {
        mm.kill();

        modelTimeline?.scrollTrigger?.kill();
        modelTimeline?.kill();

        pinTimeline?.scrollTrigger?.kill();
        pinTimeline?.kill();
      };
    }
  }, [duckRef, gotResponsive, windowWidth]);

  return (
    <>
      {gotResponsive && (
        <group
          ref={duckRef}
          scale={scale}
          position={[positionX, positionY, 0]}
          rotation={[0, rotationY, 0]}
        >
          <CommonDuck
            cursorAnimations
            modelData={modelData.current}
          ></CommonDuck>
        </group>
      )}
    </>
  );
};
